import { useUpdateEstView } from "@entities/brief/lib/hooks/useAddEstViews";
import { ToastService } from "@shared/lib/services";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button, Card, Dropdown, Popover } from "@shared/ui";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Flex, Form, InputNumber } from "antd";
import { Ellipsis } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

export default function BrandCPV({
  briefInfluencer,
  updateBriefInfluencer,
}: {
  briefInfluencer: IBriefInfluencerV2;
  updateBriefInfluencer: (id: string, data: IBriefInfluencerV2) => void;
}) {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [dropdownState, setDropdownState] = useState<boolean>(false);
  const [editEstViews, setEditEstViews] = useState<{
    briefInfluencerId: string;
    estViews: number;
    open: boolean;
  }>({
    briefInfluencerId: "",
    estViews: 0,
    open: false,
  });
  const [form] = Form.useForm();

  const views = Form.useWatch("estViews", form) || 1;

  const cost = briefInfluencer?.brandCost?.cost || 0;
  const cpv = views ? (cost / views).toFixed(2) : "-";

  const initialViews = briefInfluencer?.estViews || 1;
  const initialCpv = initialViews ? (cost / initialViews).toFixed(2) : "-";

  const {
    updateEstView,
    isUpdating: isEstViewUpdating,
    updateEstViewError,
  } = useUpdateEstView(undefined, () => {
    updateBriefInfluencer(briefInfluencer._id, {
      ...briefInfluencer,
      estViews: views,
    });
    ToastService.success("Estimated views updated successfully");
    setEditEstViews({
      briefInfluencerId: "",
      estViews: 0,
      open: false,
    });
  });

  useEffect(() => {
    if (updateEstViewError) {
      ToastService.error(updateEstViewError.message);
    }
  }, [updateEstViewError]);

  useEffect(() => {
    setEditEstViews({
      briefInfluencerId: briefInfluencer._id,
      estViews: briefInfluencer?.estViews || 0,
      open: false,
    });
    form.setFieldValue("estViews", views);
  }, [briefInfluencer]);
  return (
    <Flex
      align="center"
      gap={5}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        if (!editEstViews.open && !dropdownState) {
          setIsHovered(false);
        }
      }}
    >
      <Flex vertical>
        <TextWithTooltip limit={6} text={initialCpv} textClassName={styles.cpvValue} />
        <span className={styles.cpvViews}>{initialViews} views</span>
      </Flex>
      <Popover
        trigger={["click"]}
        open={editEstViews.open}
        destroyTooltipOnHide
        placement="right"
        title="Edit Est. Views"
        onOpenChange={(open) => {
          if (!open) {
            setDropdownState(false);
            setEditEstViews((prev) => ({
              open: false,
              briefInfluencerId: "",
              estViews: 0,
            }));
          }
        }}
        content={
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => {
              updateEstView({
                briefInfluencerId: briefInfluencer._id,
                estViews: isNaN(values.estViews)
                  ? 0
                  : parseInt(values.estViews),
              });
            }}
          >
            <Flex vertical gap={0} style={{ height: "170px" }}>
              <Form.Item
                name="estViews"
                rules={[
                  {
                    required: true,
                    message: "Please enter estimated views",
                  },
                ]}
                style={{ marginBottom: 2 }}
              >
                <InputNumber
                  placeholder="Enter Est. Views"
                  type="number"
                  min={0}
                />
              </Form.Item>

              <Card
                style={{
                  marginTop: 10,
                  marginBottom: 10,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e3e8ee",
                }}
                styles={{
                  body: {
                    padding: 10,
                  },
                }}
              >
                <Flex
                  vertical
                  gap={10}
                  style={{
                    fontSize: 11,
                    color: "#697386",
                  }}
                >
                  <span>
                    Brand CPV: <b> {cpv} </b>
                  </span>
                  <span>
                    Brand Commercial:{" "}
                    <b> ₹{briefInfluencer?.brandCost?.cost || "0"} </b>
                  </span>
                </Flex>
              </Card>
              <Form.Item>
                <Flex gap={10} justify="flex-end">
                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setEditEstViews({
                        briefInfluencerId: "",
                        estViews: 0,
                        open: false,
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    htmlType="submit"
                    type="primary"
                    size="small"
                    loading={isEstViewUpdating}
                  >
                    Save
                  </Button>
                </Flex>
              </Form.Item>
            </Flex>
          </Form>
        }
      >
        <Dropdown
          trigger={["click"]}
          open={dropdownState}
          onOpenChange={(open) => setDropdownState(open)}
          menu={{
            items: [
              {
                label: "Edit Estimated Views",
                key: "edit",
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") {
                setEditEstViews({
                  briefInfluencerId: briefInfluencer._id,
                  estViews: views,
                  open: true,
                });
              }
            },
          }}
        >
          {isHovered ? (
            <Button icon={<Ellipsis size={14} />} type="text" size="small" />
          ) : null}
        </Dropdown>
      </Popover>
    </Flex>
  );
}
